/**
 * Cross-platform environment variable access
 * Supports both Vite (import.meta.env.VITE_*) and Next.js (process.env.NEXT_PUBLIC_*)
 *
 * Environment variables are cached at module initialization for performance.
 */

type EnvKey =
  | 'BACKEND_URL'
  | 'GOOGLE_MAPS_API_KEY'
  | 'STRIPE_PK'
  | 'RECAPTCHA_SITE_KEY'
  | 'HOMEPOINT_FEE'
  | 'APP_VERSION'
  | 'POSTHOG_KEY'
  | 'POSTHOG_HOST'
  | 'MARKETING_URL';

const defaults: Partial<Record<EnvKey, string>> = {
  BACKEND_URL: 'http://localhost:3030',
  HOMEPOINT_FEE: '5000',
  APP_VERSION: '0.0.0',
  MARKETING_URL: 'https://homepoint.co',
};

/**
 * Get Next.js env var by key.
 * Next.js requires literal string references for NEXT_PUBLIC_* vars to be inlined at build time.
 */
function getNextEnv(key: EnvKey): string | undefined {
  if (typeof process === 'undefined' || !process.env) return undefined;

  // Must use literal strings for Next.js static replacement
  switch (key) {
    case 'BACKEND_URL':
      return process.env.NEXT_PUBLIC_BACKEND_URL;
    case 'GOOGLE_MAPS_API_KEY':
      return process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
    case 'STRIPE_PK':
      return process.env.NEXT_PUBLIC_STRIPE_PK;
    case 'RECAPTCHA_SITE_KEY':
      return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    case 'HOMEPOINT_FEE':
      return process.env.NEXT_PUBLIC_HOMEPOINT_FEE;
    case 'APP_VERSION':
      return process.env.NEXT_PUBLIC_APP_VERSION;
    case 'POSTHOG_KEY':
      return process.env.NEXT_PUBLIC_POSTHOG_KEY;
    case 'POSTHOG_HOST':
      return process.env.NEXT_PUBLIC_POSTHOG_HOST;
    case 'MARKETING_URL':
      return process.env.NEXT_PUBLIC_MARKETING_URL;
    default:
      return undefined;
  }
}

/**
 * Resolve an environment variable value (internal, uncached)
 */
function resolveEnv(key: EnvKey): string {
  // Try Vite env vars (import.meta.env)
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    const viteKey = `VITE_${key}`;
    const vitePublicKey = `VITE_PUBLIC_${key}`;
    const viteValue =
      import.meta.env[viteKey] || import.meta.env[vitePublicKey];
    if (viteValue) return viteValue;
  }

  // Try Next.js env vars (process.env) - uses literal strings for static replacement
  const nextValue = getNextEnv(key);
  if (nextValue) return nextValue;

  // Return default or empty string
  return defaults[key] || '';
}

// Cache all environment variables at module initialization
const envCache: Record<EnvKey, string> = {
  BACKEND_URL: resolveEnv('BACKEND_URL'),
  GOOGLE_MAPS_API_KEY: resolveEnv('GOOGLE_MAPS_API_KEY'),
  STRIPE_PK: resolveEnv('STRIPE_PK'),
  RECAPTCHA_SITE_KEY: resolveEnv('RECAPTCHA_SITE_KEY'),
  HOMEPOINT_FEE: resolveEnv('HOMEPOINT_FEE'),
  APP_VERSION: resolveEnv('APP_VERSION'),
  POSTHOG_KEY: resolveEnv('POSTHOG_KEY'),
  POSTHOG_HOST: resolveEnv('POSTHOG_HOST'),
  MARKETING_URL: resolveEnv('MARKETING_URL'),
};

/**
 * Get an environment variable with cross-platform support.
 * Values are cached at module initialization for optimal performance.
 */
export function getEnv(key: EnvKey): string {
  return envCache[key];
}

/**
 * Check if we're running in a browser environment
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Check if we're running in Vite
 */
export function isVite(): boolean {
  return typeof import.meta !== 'undefined' && import.meta.env !== undefined;
}

/**
 * Check if we're running in Next.js
 */
export function isNextJs(): boolean {
  return (
    typeof process !== 'undefined' && process.env?.NEXT_RUNTIME !== undefined
  );
}
